import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, CheckCircle, Download, Copy, Send, RefreshCw, RotateCcw, AlertTriangle,
  Film, Sparkles, TrendingDown, BadgeCheck, HardDriveDownload, PencilLine,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TERMINAL = ["succeeded", "failed"];
const KIND_ICON = { sneak_peek: Film, just_listed: Sparkles, price_reduction: TrendingDown, recently_sold: BadgeCheck };

const slug = (s) => (s || "social").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "social";

export const jobAsVideo = (job, property) => ({
  id: job.job_id,
  kind: job.kind,
  secure_url: job.r2_url || job.url,
  format_key: "portrait_9_16",
  format_label: job.label,
  filename: `${slug(property?.name)}_${job.kind}.mp4`,
});

const BUSY_COPY = {
  preparing: ["Preparing clips…", "Matching colours (HDR → SDR) — up to a minute the first time"],
  submitting: ["Sending to the renderer…", "A few seconds"],
};

function StatusChip({ job }) {
  if (job.status === "succeeded") return <span className="cmb-chip is-done" data-testid={`cmb-status-${job.kind}`}><CheckCircle size={12} /> Ready</span>;
  if (job.status === "failed") return <span className="cmb-chip is-failed" data-testid={`cmb-status-${job.kind}`}><AlertTriangle size={12} /> Failed</span>;
  return <span className="cmb-chip is-busy" data-testid={`cmb-status-${job.kind}`}><Loader2 size={12} className="spin-icon" /> {job.status === "preparing" ? "preparing clips" : (job.status || "queued")}</span>;
}

function OutputCard({ job, property, submitted, submitting, onApprove, onCopyLink, canHubPush, pushed, onHubPush, onRetry }) {
  const Icon = KIND_ICON[job.kind] || Film;
  const video = jobAsVideo(job, property);
  const ready = job.status === "succeeded" && video.secure_url;
  return (
    <div className={`cmb-card ${job.status}`} data-testid={`cmb-card-${job.kind}`}>
      <div className="cmb-card-head">
        <span className="cmb-card-title"><Icon size={15} /> {job.label}</span>
        <StatusChip job={job} />
      </div>
      <div className="cmb-card-sub">
        {job.template_name || (job.kind === "sneak_peek" ? "Plain edit" : "Reels Styler master")}
        {job.width && job.height ? ` · ${job.width}×${job.height}` : ""}
      </div>
      <div className="cmb-frame">
        {ready ? (
          <video src={video.secure_url} controls preload="metadata" poster={job.snapshot_url || undefined} data-testid={`cmb-video-${job.kind}`} />
        ) : job.status === "failed" ? (
          <div className="cmb-state is-failed" data-testid={`cmb-failed-${job.kind}`}>
            <AlertTriangle size={26} />
            <span>{job.error || "Render failed"}</span>
            <button type="button" className="cmb-retry" onClick={() => onRetry(job)} data-testid={`cmb-retry-${job.kind}`}>
              <RotateCcw size={13} /> Retry
            </button>
          </div>
        ) : (
          <div className="cmb-state" data-testid={`cmb-rendering-${job.kind}`}>
            <Loader2 size={28} className="spin-icon" />
            <span>{(BUSY_COPY[job.status] || ["Rendering…"])[0]}</span>
            <span className="cmb-state-sub">{(BUSY_COPY[job.status] || ["", "Usually 30–90 seconds"])[1]}</span>
          </div>
        )}
      </div>
      <div className="cmb-meta">
        {job.clip_count ? `${job.clip_count} clips` : ""}
        {job.total_duration ? ` · ${Math.round(job.total_duration)}s` : ""}
        {job.music_name ? <span className="cmb-music" data-testid={`cmb-music-${job.kind}`}> · ♪ {job.music_name}{job.own_music ? " (own track)" : ""}</span> : ""}
        {job.render_seconds ? ` · rendered in ${Math.round(job.render_seconds)}s` : ""}
        {job.r2_url && <span className="cmb-saved"><HardDriveDownload size={12} /> Saved to NicheCom storage</span>}
      </div>
      {ready && (
        <div className="video-actions cmb-actions">
          <a href={video.secure_url} download={video.filename} className="video-btn" target="_blank" rel="noopener noreferrer" title="Download" data-testid={`cmb-download-${job.kind}`}>
            <Download className="video-btn-icon" /><span>Download</span>
          </a>
          {submitted ? (
            <button className="video-btn approve-btn submitted" onClick={() => onCopyLink(video)} data-testid={`cmb-copy-${job.kind}`} title="Copy link">
              {submitted.r2_url ? <><CheckCircle size={14} /><span>Copy R2 Link</span></> : <><Copy size={14} /><span>Copy Link</span></>}
            </button>
          ) : (
            <button className="video-btn approve-btn" onClick={() => onApprove(video)} disabled={submitting} data-testid={`cmb-approve-${job.kind}`} title="Submit / Approve">
              {submitting ? <><Loader2 size={14} className="spin-icon" /><span>Submitting…</span></> : <><CheckCircle size={14} /><span>Submit / Approve</span></>}
            </button>
          )}
          {canHubPush && (
            <button className={`video-btn hub-push-btn ${pushed ? "pushed" : ""}`} onClick={() => !pushed && onHubPush(video)} disabled={!!pushed} data-testid={`cmb-hub-${job.kind}`} title="Send to Social Library">
              {pushed ? <><CheckCircle size={14} /><span>In Social Library</span></> : <><Send size={14} /><span>Social Library</span></>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CreatomateBatchResults({
  batch, onUpdate, onBack, submittedVideos, submittingVideo, onApprove, onCopyLink,
  canHubPush, hubPushed, onHubPush, onReopenEdit,
}) {
  const jobs = batch.jobs || [];
  const busy = jobs.some((j) => !TERMINAL.includes(j.status));
  const readyCount = jobs.filter((j) => j.status === "succeeded").length;
  const prevStatus = useRef({});

  useEffect(() => {
    jobs.forEach((j) => {
      const prev = prevStatus.current[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === "succeeded") toast.success(`${j.label} is ready`);
        if (j.status === "failed") toast.error(`${j.label} failed: ${j.error || "unknown error"}`);
      }
      prevStatus.current[j.job_id] = j.status;
    });
  }, [jobs]);

  useEffect(() => {
    if (!batch.batch_id || !busy) return;
    const timer = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/video/creatomate/batches/${batch.batch_id}`);
        onUpdate((prev) => ({ ...prev, ...r.data }));
      } catch (_) { /* transient */ }
    }, 4000);
    return () => clearInterval(timer);
  }, [batch.batch_id, busy, onUpdate]);

  const retry = async (job) => {
    try {
      const r = await axios.post(`${API}/video/creatomate/jobs/${job.job_id}/retry`);
      onUpdate((prev) => ({ ...prev, jobs: prev.jobs.map((j) => (j.job_id === job.job_id ? { ...j, ...r.data } : j)) }));
      toast.info(`${job.label} re-submitted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };

  return (
    <div id="video-results" className="results-section cmb" data-testid="creatomate-batch-results">
      <div className="results-header">
        <div>
          <h2 className="results-title">Social Outputs</h2>
          <div className="cmb-subtitle" data-testid="cmb-progress">
            {busy ? <Loader2 size={13} className="spin-icon" /> : <CheckCircle size={13} />}
            <span>
              {readyCount} of {jobs.length} ready{batch.property?.name ? ` · ${batch.property.name}` : ""}
              {batch.version > 1 ? ` · v${batch.version}` : ""}
              {batch.created_by_name ? ` · ${batch.created_by_name}` : ""}
            </span>
          </div>
        </div>
        <div className="results-actions">
          {onReopenEdit && (
            <button className="generate-more-btn cmb-back cmb-reopen" onClick={onReopenEdit} data-testid="results-reopen-edit-btn" title="Restore this edit to tweak and re-render">
              <PencilLine size={16} /> Reopen edit
            </button>
          )}
          <button className="generate-more-btn cmb-back" onClick={onBack} data-testid="generate-more-btn">
            <RefreshCw size={16} /> Generate More
          </button>
        </div>
      </div>
      <div className="cmb-grid">
        {jobs.map((job) => (
          <OutputCard
            key={job.job_id}
            job={job}
            property={batch.property}
            submitted={submittedVideos[job.job_id]}
            submitting={submittingVideo === job.job_id}
            onApprove={onApprove}
            onCopyLink={onCopyLink}
            canHubPush={canHubPush}
            pushed={hubPushed[job.job_id]}
            onHubPush={onHubPush}
            onRetry={retry}
          />
        ))}
      </div>
    </div>
  );
}
